import { ScrollView, StyleSheet, Text, View } from "react-native";
// Vendor amounts are ISO 4217 minor units of their currency (money.ts).
import { merchDeliveryLabel, storeMoneyMinor as money } from "@libs/platform-wire";
import type { DeliveryOption } from "@libs/buy-on-behalf-client";
import {
  deliveryArrives,
  deliveryDutiesNote,
  deliveryFeeLabel,
  deliveryProblem,
  type DeliveryQuote,
} from "@libs/buy-on-behalf-client/delivery";
import LPressable from "@components/LPressable";
import { Chevron } from "../native/Chevron";
import { colors } from "../native/colors";
import { OrderSummaryView } from "../native/OrderSummaryCard";

// A merch order's delivery in the app's purchase sheet, as Link in Bio's
// draws it (libs/shared-ui merch-delivery.tsx): the line under what is
// chosen while the store is asked, and the Delivery step when it offers
// more than one option.

/**
 * Under what is chosen: "UK delivery" and its fee once the store has
 * answered (from the cheapest when it offers several), "Getting delivery…"
 * meanwhile, or why the basket cannot be posted, with "Try again" when the
 * store did not answer.
 */
export function DeliveryQuoteNote({
  quote,
  country,
  currency,
  nameOf,
  onRetry,
}: {
  quote: DeliveryQuote;
  country?: string | null;
  currency: string;
  nameOf: (itemId: string) => string;
  onRetry: () => void;
}) {
  if (quote.status === "idle") return null;
  const problem = deliveryProblem(quote, nameOf);
  if (problem)
    return (
      <View accessibilityRole="alert" style={styles.line} testID="merch-delivery">
        <Text style={[styles.text, styles.problem]}>{problem}</Text>
        {quote.status === "failed" ? (
          <LPressable
            accessibilityRole="button"
            hitSlop={8}
            onPress={onRetry}
            sentryEventName="VendorTicketsSheet.DeliveryRetry"
          >
            <Text style={[styles.text, styles.retry]}>Try again</Text>
          </LPressable>
        ) : null}
      </View>
    );
  const cheapest = quote.status === "ready" ? quote.options[0] : undefined;
  const several = quote.status === "ready" && quote.options.length > 1;
  const label =
    merchDeliveryLabel(country, !!cheapest && cheapest.feeMinor === 0 && !several);
  const amount = !cheapest
    ? "Getting delivery…"
    : several
      ? `from ${deliveryFeeLabel(cheapest, currency)}`
      : cheapest.feeMinor > 0
        ? money(cheapest.feeMinor, currency)
        : null;
  // Its one option leaves import duties to the fan: said under it.
  const duties = several ? null : deliveryDutiesNote(cheapest);
  const line = (
    <View
      accessible
      accessibilityLabel={[label, amount].filter(Boolean).join(" ")}
      style={styles.line}
      testID="merch-delivery"
    >
      <Text style={styles.text}>{label}</Text>
      {amount ? <Text style={styles.text}>{amount}</Text> : null}
    </View>
  );
  return duties ? (
    <View>
      {line}
      <Text style={[styles.text, styles.duties]} testID="delivery-duties">
        {duties}
      </Text>
    </View>
  ) : (
    line
  );
}

/**
 * The Delivery step: each option the store offers, when it arrives and
 * what the order then costs, the chosen one marked (the cheapest until the
 * fan chooses). The sheet's button continues with it.
 */
export function DeliveryChoiceView({
  order,
  options,
  chosen,
  goodsMinor,
  currency,
  onChoose,
  onBack,
}: {
  order: { title: string; detail: string; total: string };
  options: DeliveryOption[];
  chosen: DeliveryOption;
  goodsMinor: number;
  currency: string;
  onChoose: (option: DeliveryOption) => void;
  onBack: () => void;
}) {
  return (
    <View style={styles.step} testID="vendor-delivery-step">
      <View style={styles.header}>
        <LPressable
          accessibilityLabel="Back"
          accessibilityRole="button"
          hitSlop={10}
          onPress={onBack}
          sentryEventName="VendorTicketsSheet.DeliveryBack"
          style={styles.back}
        >
          <Chevron />
        </LPressable>
        <Text accessibilityRole="header" style={styles.title}>
          Delivery
        </Text>
      </View>
      <View style={styles.summary}>
        <OrderSummaryView fee={null} {...order} />
      </View>
      <ScrollView
        accessibilityRole="radiogroup"
        accessibilityLabel="Delivery"
        alwaysBounceVertical={false}
        contentContainerStyle={styles.options}
        showsVerticalScrollIndicator={false}
        style={styles.scroll}
      >
        {options.map((option) => {
          const selected = option.code === chosen.code;
          const arrives = deliveryArrives(option);
          const duties = deliveryDutiesNote(option);
          return (
            <LPressable
              key={option.code}
              accessibilityRole="radio"
              accessibilityLabel={[option.name, deliveryFeeLabel(option, currency), arrives, duties, `Total ${money(goodsMinor + option.feeMinor, currency)}`].filter(Boolean).join(", ")}
              accessibilityState={{ checked: selected }}
              onPress={() => onChoose(option)}
              sentryEventName="VendorTicketsSheet.DeliveryOption"
              style={selected ? { ...styles.option, ...styles.optionChosen } : styles.option}
              testID={`delivery-option-${option.code}`}
            >
              <View style={[styles.dot, selected ? styles.dotChosen : null]}>
                {selected ? <View style={styles.dotFill} /> : null}
              </View>
              <View style={styles.optionWords}>
                <Text style={styles.optionName}>{option.name}</Text>
                {arrives ? <Text style={styles.text}>{arrives}</Text> : null}
                {duties ? (
                  <Text style={styles.text} testID="delivery-duties">
                    {duties}
                  </Text>
                ) : null}
                <Text style={styles.text}>
                  Total {money(goodsMinor + option.feeMinor, currency)}
                </Text>
              </View>
              <Text style={styles.optionName}>{deliveryFeeLabel(option, currency)}</Text>
            </LPressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  line: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 12,
    paddingHorizontal: 14,
  },
  // The light ticket rows' secondary text.
  text: { color: colors.secondary, fontSize: 13 },
  problem: { color: colors.danger, flexShrink: 1 },
  duties: { paddingHorizontal: 14 },
  retry: { color: colors.primary, fontWeight: "600" },
  header: { alignItems: "center", flexDirection: "row", gap: 6, marginBottom: 12, minHeight: 28 },
  back: { alignItems: "center", height: 28, justifyContent: "center", marginLeft: -6, width: 28 },
  title: { color: colors.text, fontSize: 20, fontWeight: "700" },
  summary: { marginBottom: 12 },
  step: { flexShrink: 1 },
  scroll: { flexShrink: 1 },
  options: { gap: 8 },
  option: {
    alignItems: "center",
    backgroundColor: colors.fill,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: colors.fill,
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  optionChosen: { borderColor: colors.primary },
  optionWords: { flex: 1, minWidth: 0, gap: 1 },
  optionName: { color: colors.text, fontSize: 14, fontWeight: "600" },
  dot: {
    alignItems: "center",
    borderColor: colors.border,
    borderRadius: 10,
    borderWidth: 2,
    height: 20,
    justifyContent: "center",
    width: 20,
  },
  dotChosen: { borderColor: colors.primary },
  dotFill: { backgroundColor: colors.primary, borderRadius: 5, height: 10, width: 10 },
});
